import { Box, Heading, TabList, TabPanel, TabPanels, Tabs, Tab as BaseTab } from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store';
import { BoxMain, Header, MainBox, Tab } from 'components';
import {
  appContextSelector,
  isCorporateThemeSelector,
  loadAdditionalTheme,
  partnershipIdSelector,
  setContext,
  setPartnershipId,
  unloadAdditionalTheme,
} from 'modules/app/appSlice';
import BoxContent from 'components/BoxContent/corporate';
import useBreakpoint from 'hooks/useBreakpoint';
import { useHistory, useLocation } from 'react-router-dom';
import GeneralTerms from 'pages/shared/Settings/TermsAndConditions/GeneralTerms';
import WeChoooseTerms from 'pages/shared/Settings/TermsAndConditions/WeChoooseTerms';
import PartnerTerms from 'pages/shared/Settings/TermsAndConditions/PartnerTerms';
import PolicyTerms from 'pages/shared/Settings/TermsAndConditions/PolicyTerms';
import CookiePolicyTerms from 'pages/shared/Settings/TermsAndConditions/CookiePolicy';
import qs from 'qs';
import { getPartnerSettings } from 'utils/partnerSettings';
import { loadAllTerms } from 'modules/terms/termsSlice';

const wechoooseTermTypes = ['general', 'wechooose', 'partner'];

const mychoooseTermTypes = ['privacy', 'cookie'];

export type TermsTheme = 'chooosepersonal' | 'chooosecompany' | 'partnerpersonal' | 'partnercompany';

type TermsSet = 'company' | 'personal';

const GlobalTerms: FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation('settings');
  const dispatch = useDispatch();
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const history = useHistory();
  const location = useLocation();
  const [index, setIndex] = useState(location.hash.includes('cookies') ? 1 : 0);
  const [termsSet, setTermsSet] = useState<TermsSet>('company');
  const { isMobile } = useBreakpoint();
  const appContext = useSelector(appContextSelector);
  const { generalTermsTitle, wechoooseTermsTitle, partnerTermsTitle, cookieTermsTitle, policyTermsTitle } = useSelector(
    (state: RootState) => state.terms,
  );

  const partnershipId = useSelector(partnershipIdSelector);

  const StyledTab = isCorporateTheme ? BaseTab : Tab;

  const params: {
    terms?: string;
    context?: string;
    partnershipId?: string;
    theme?: string;
    set?: string;
    included?: string;
  } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const shouldDisplayCompanyTerms = termsSet !== 'personal';
  const termTypes = shouldDisplayCompanyTerms ? wechoooseTermTypes : mychoooseTermTypes;
  const isPartnershipIdAvailable = params.partnershipId || partnershipId;
  const isDeprecatedStyle = !!params.context || !!params.set;
  const shouldDisplayPartnerTerms = isPartnershipIdAvailable && partnerTermsTitle;
  const availableTermTypes = [
    { key: 'general', title: generalTermsTitle, Component: GeneralTerms },
    { key: 'portal', title: wechoooseTermsTitle, Component: WeChoooseTerms },
    { key: 'partner', title: partnerTermsTitle, Component: PartnerTerms },
    { key: 'privacy', title: policyTermsTitle, Component: PolicyTerms },
    { key: 'cookie', title: cookieTermsTitle, Component: CookiePolicyTerms },
  ];

  const defaultSets = ['general', 'privacy', ...(shouldDisplayPartnerTerms ? ['partner'] : [])];

  const parsedIncludedSets = params.included?.split(',');

  const displayedSets = parsedIncludedSets ?? defaultSets;

  const filteredTermTypes = displayedSets.map(setName => availableTermTypes.find(termType => termType.key === setName)).filter(x => !!x) ?? [];

  useEffect(() => {
    const settings = getPartnerSettings();
    if (!partnershipId) {
      if (params.partnershipId) {
        dispatch(setPartnershipId(params.partnershipId));
        dispatch(loadAllTerms(params.partnershipId));
      } else if (settings.partnershipId) {
        dispatch(setPartnershipId(settings.partnershipId));
        dispatch(loadAllTerms(settings.partnershipId));
      }
    }
  }, [isCorporateTheme, language]);

  useEffect(() => {
    if (params.partnershipId) {
      dispatch(loadAdditionalTheme(params.partnershipId));
    }

    return () => {
      if (params.partnershipId) {
        dispatch(unloadAdditionalTheme());
      }
    };
  }, [params.partnershipId]);

  useEffect(() => {
    if (params.set) {
      setTermsSet(params.set as TermsSet);
    } else {
      setTermsSet(appContext === 'wechooose' ? 'company' : 'personal');
    }
  }, [params.set, appContext]);

  useEffect(() => {
    if (params.context) {
      if (appContext !== params.context) {
        dispatch(setContext(params.context as PortalContext));
      }
    } else if (params.theme) {
      switch (params.theme as TermsTheme) {
        case 'chooosecompany':
        // TODO: fallthrough for now, to be expanded later
        case 'chooosepersonal':
          dispatch(setContext('mychooose'));
          break;
        case 'partnercompany':
        // TODO: fallthrough for now, to be expanded later
        case 'partnerpersonal':
          dispatch(setContext('wechooose'));
          break;
        default:
          dispatch(setContext('wechooose'));
      }
    } else if (appContext !== 'wechooose') {
      dispatch(setContext('wechooose'));
    }

    if (params.terms && termTypes.includes(params.terms) && isDeprecatedStyle) {
      setIndex(termTypes.indexOf(params.terms));
    } else if (params.terms && displayedSets.includes(params.terms)) {
      setIndex(displayedSets.indexOf(params.terms));
    }
  }, [params.context, params.terms, termTypes, displayedSets, params.theme]);

  const onTabChange = (idx: number): void => {
    history.push({
      search: qs.stringify({
        theme: params.theme,
        context: params.context,
        terms: isDeprecatedStyle ? termTypes[idx] : filteredTermTypes?.[idx]?.key,
        partnershipId: params.partnershipId,
        set: params.set,
        included: params.included,
      }),
    });
  };

  const deprecatedTabStructure = shouldDisplayCompanyTerms ? (
    <>
      <TabList mb={['34px', '60px']} flexDirection={isMobile ? 'column' : 'row'}>
        <StyledTab>{generalTermsTitle}</StyledTab>
        <StyledTab>{wechoooseTermsTitle}</StyledTab>
        {shouldDisplayPartnerTerms && partnerTermsTitle && <StyledTab>{partnerTermsTitle}</StyledTab>}
      </TabList>
      <TabPanels>
        <TabPanel p="0px">
          <GeneralTerms />
        </TabPanel>
        <TabPanel p="0px">
          <WeChoooseTerms />
        </TabPanel>
        {shouldDisplayPartnerTerms && partnerTermsTitle && (
          <TabPanel p="0px">
            <PartnerTerms />
          </TabPanel>
        )}
      </TabPanels>
    </>
  ) : (
    <>
      <TabList mb={['34px', '60px']} flexDirection={isMobile ? 'column' : 'row'}>
        <StyledTab>{t('terms.privacy')}</StyledTab>
        <StyledTab>{t('terms.cookie')}</StyledTab>
      </TabList>
      <TabPanels>
        <TabPanel p="0px">
          <PolicyTerms />
        </TabPanel>
        <TabPanel p="0px">
          <CookiePolicyTerms />
        </TabPanel>
      </TabPanels>
    </>
  );

  const newTabStructure = (
    <>
      <TabList mb={['34px', '60px']} flexDirection={isMobile ? 'column' : 'row'}>
        {filteredTermTypes.map(termType => (
          <StyledTab key={termType?.key}>{termType?.title}</StyledTab>
        ))}
      </TabList>
      <TabPanels>
        {filteredTermTypes.map(termType => (
          <TabPanel p="0px" key={termType?.key}>
            {termType ? <termType.Component /> : null}
          </TabPanel>
        ))}
      </TabPanels>
    </>
  );

  return (
    <>
      <Header />
      <MainBox>
        <BoxContent>
          <BoxMain>
            <Box p="24px">
              <Heading as="h3" mb={['40px', '40px', '36px']} fontSize="lg" fontWeight={isCorporateTheme ? 400 : 500} color="secondary.800">
                {t('routes.terms')}
              </Heading>
              <Tabs index={index} onChange={onTabChange}>
                {isDeprecatedStyle ? deprecatedTabStructure : newTabStructure}
              </Tabs>
            </Box>
          </BoxMain>
        </BoxContent>
      </MainBox>
    </>
  );
};

export default GlobalTerms;
